import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendingUp, DollarSign, Calendar, Award, Plus, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const VentasDashboard = ({ orgId }) => {
  const [ventas, setVentas] = useState([]);
  const [periodo, setPeriodo] = useState('mes'); // dia, semana, mes, año
  const [stats, setStats] = useState({ total: 0, count: 0, promedio: 0, comisiones: 0 });
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    propiedad: '',
    comprador: '',
    vendedor: '',
    precio: '',
    comision: '',
    fecha: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadVentas();
  }, [periodo, orgId]);

  const loadVentas = async () => {
    try {
      const res = await axios.get(`${API}/organizations/${orgId}/ventas?periodo=${periodo}`);
      setVentas(res.data.ventas || []);
      calculateStats(res.data.ventas || []);
    } catch (error) {
      console.error('Error al cargar ventas:', error);
      setVentas([]);
    }
  };

  const calculateStats = (ventasData) => {
    const total = ventasData.reduce((sum, v) => sum + (v.precio || 0), 0);
    const comisiones = ventasData.reduce((sum, v) => sum + (v.comision || 0), 0);
    const count = ventasData.length;
    const promedio = count > 0 ? total / count : 0;
    setStats({ total, count, promedio, comisiones });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/organizations/${orgId}/ventas`, {
        ...formData,
        precio: parseFloat(formData.precio),
        comision: parseFloat(formData.comision)
      });
      toast.success('✅ Operación registrada');
      setShowForm(false);
      setFormData({
        propiedad: '',
        comprador: '',
        vendedor: '',
        precio: '',
        comision: '',
        fecha: new Date().toISOString().split('T')[0]
      });
      loadVentas();
    } catch (error) {
      toast.error('Error al registrar operación');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-white">Operaciones Completadas</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-bold flex items-center gap-2 hover:opacity-90 transition"
        >
          <Plus size={20} />
          Añadir Operación
        </button>
      </div>

      {/* Period Selector */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => setPeriodo('dia')}
          className={`px-5 py-2.5 rounded-lg font-bold transition ${
            periodo === 'dia' 
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
              : 'bg-gray-800 text-white/60 hover:text-white'
          }`}
        >
          Últimas 24h
        </button>
        <button
          onClick={() => setPeriodo('semana')}
          className={`px-5 py-2.5 rounded-lg font-bold transition ${
            periodo === 'semana' 
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
              : 'bg-gray-800 text-white/60 hover:text-white'
          }`}
        >
          Última Semana
        </button>
        <button
          onClick={() => setPeriodo('mes')}
          className={`px-5 py-2.5 rounded-lg font-bold transition ${
            periodo === 'mes' 
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
              : 'bg-gray-800 text-white/60 hover:text-white'
          }`}
        >
          Este Mes
        </button>
        <button
          onClick={() => setPeriodo('año')}
          className={`px-5 py-2.5 rounded-lg font-bold transition ${
            periodo === 'año' 
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
              : 'bg-gray-800 text-white/60 hover:text-white'
          }`}
        >
          Último Año
        </button>
      </div>

      {/* Add Operation Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-900 border-2 border-green-500 rounded-xl p-6 overflow-hidden"
          >
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-bold text-white">Nueva Operación</h4>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white text-sm font-bold mb-2 block">Propiedad</label>
                  <input
                    type="text"
                    value={formData.propiedad}
                    onChange={(e) => setFormData({...formData, propiedad: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-white/20"
                    required
                    placeholder="Dirección o referencia"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-bold mb-2 block">Comprador</label>
                  <input
                    type="text"
                    value={formData.comprador}
                    onChange={(e) => setFormData({...formData, comprador: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-white/20"
                    required
                    placeholder="Nombre del comprador"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-bold mb-2 block">Vendedor/Agente</label>
                  <input
                    type="text"
                    value={formData.vendedor}
                    onChange={(e) => setFormData({...formData, vendedor: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-white/20"
                    required
                    placeholder="Nombre del vendedor"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-bold mb-2 block">Precio de Venta (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.precio}
                    onChange={(e) => setFormData({...formData, precio: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-white/20"
                    required
                    placeholder="250000"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-bold mb-2 block">Comisión (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.comision}
                    onChange={(e) => setFormData({...formData, comision: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-white/20"
                    required
                    placeholder="7500"
                  />
                </div>
                <div>
                  <label className="text-white text-sm font-bold mb-2 block">Fecha</label>
                  <input
                    type="date"
                    value={formData.fecha}
                    onChange={(e) => setFormData({...formData, fecha: e.target.value})}
                    className="w-full bg-gray-800 text-white px-4 py-3 rounded-lg border border-white/20"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-bold hover:opacity-90 transition"
                >
                  Guardar Operación
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-6 bg-gray-700 text-white py-3 rounded-lg font-bold hover:bg-gray-600"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-green-600 to-emerald-600 p-6 rounded-xl text-white"
        >
          <div className="flex items-center gap-3 mb-2">
            <DollarSign size={28} />
            <span className="text-sm opacity-80">Total Ventas</span>
          </div>
          <div className="text-3xl font-bold">€{stats.total.toLocaleString()}</div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-br from-yellow-600 to-orange-600 p-6 rounded-xl text-white"
        >
          <div className="flex items-center gap-3 mb-2">
            <Award size={28} />
            <span className="text-sm opacity-80">Comisiones</span>
          </div>
          <div className="text-3xl font-bold">€{stats.comisiones.toLocaleString()}</div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-blue-600 to-cyan-600 p-6 rounded-xl text-white"
        >
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={28} />
            <span className="text-sm opacity-80">Operaciones</span>
          </div>
          <div className="text-3xl font-bold">{stats.count}</div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-br from-purple-600 to-pink-600 p-6 rounded-xl text-white"
        >
          <div className="flex items-center gap-3 mb-2">
            <Calendar size={28} />
            <span className="text-sm opacity-80">Promedio</span>
          </div>
          <div className="text-3xl font-bold">€{stats.promedio.toLocaleString(undefined, {maximumFractionDigits: 0})}</div>
        </motion.div>
      </div>

      {/* Ventas List */}
      <div className="bg-gray-900 rounded-xl p-6">
        <h4 className="text-xl font-bold text-white mb-4">Historial de Operaciones</h4>
        {ventas.length === 0 ? (
          <div className="text-center py-12">
            <TrendingUp size={48} className="mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">No hay operaciones registradas en este período</p>
          </div>
        ) : (
          <div className="space-y-3">
            {ventas.map((venta, idx) => (
              <div key={idx} className="bg-gray-800 border border-white/10 rounded-lg p-4 hover:border-green-500 transition">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h5 className="text-white font-bold text-lg mb-1">{venta.propiedad}</h5>
                    <p className="text-gray-400 text-sm">
                      Comprador: {venta.comprador} • Vendedor: {venta.vendedor}
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(venta.fecha).toLocaleDateString('es-ES', { 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-green-400 font-bold text-xl">€{venta.precio?.toLocaleString()}</div>
                    <div className="text-yellow-400 text-sm">Comisión: €{venta.comision?.toLocaleString()}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
